import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronDown, Plus, UploadCloud, X } from 'lucide-react';
import DocumentPreview from '../../../Components/Ui/DocumentPreview';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
import toastService from '../../../services/toastService';
import {
  useGetProductByIdQuery,
  useUpdateProductMutation,
  useGetAllCategoriesQuery,
} from '../../../app/services/productApi';
import useFileUpload from '../../../hooks/fileUpload';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch product data
  const { data: productData, isLoading: loadingProduct } = useGetProductByIdQuery(id);
  // Fetch categories
  const { data: categoriesData, isLoading: loadingCategories } = useGetAllCategoriesQuery(true);
  const [updateProduct, { isLoading }] = useUpdateProductMutation();
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const { uploadFile, uploading } = useFileUpload();
  const [imageUrls, setImageUrls] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  // Form state - matching Add Product structure
  const [formData, setFormData] = useState({
    title: '',
    mrp: '',
    description: '',
    category: '',
    subcategory: '',
    sku: '',
    productCode: '',
    packagingType: {
      standard: '',
      master: '',
    },
    tax: {
      gstin: '',
      hsn_sac: '',
      cgst: '',
      sgst: '',
      igst: '',
      tax_inclusive: true,
    },
  });

  const [images, setImages] = useState([]);
  const [keyFeatures, setKeyFeatures] = useState([{ title: '', description: '' }]);

  // Category state
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Load categories data
  useEffect(() => {
    if (categoriesData?.data) {
      setCategories(categoriesData.data);
    }
  }, [categoriesData]);

  const generateRandomSKU = () => {
    const randomSKU = 'SKU-' + Math.random().toString(36).substring(2, 10).toUpperCase();
    setFormData((prev) => ({ ...prev, sku: randomSKU }));
  };

  // Replace the existing useEffect that loads product data with this fixed version:

  useEffect(() => {
    if (productData?.data && categories.length > 0) {
      const product = productData.data;
      const productCategoryId = product.category?._id || product.category;
      setExistingImages(product.images || []);

      // Since we save subcategory as category when both are provided,
      // we need to determine if the saved category is actually a subcategory
      let parentCategoryId = '';
      let subcategoryId = '';
      let subcategoriesForForm = [];

      // First, search all categories to see if productCategoryId is a subcategory
      let foundAsSubcategory = false;

      for (const category of categories) {
        if (category.children && category.children.length > 0) {
          const foundSubcategory = category.children.find((sub) => sub._id === productCategoryId);
          if (foundSubcategory) {
            // The saved category is actually a subcategory
            parentCategoryId = category._id;
            subcategoryId = productCategoryId;
            foundAsSubcategory = true;
            subcategoriesForForm = category.children; // Store subcategories for this parent

            break;
          }
        }
      }

      // If not found as subcategory, it must be a top-level category
      if (!foundAsSubcategory) {
        const topLevelCategory = categories.find(
          (cat) => cat._id === productCategoryId && !cat.parentCategory,
        );
        if (topLevelCategory) {
          parentCategoryId = productCategoryId;
          subcategoryId = '';
          subcategoriesForForm = topLevelCategory.children || [];
        }
      }

      // Set subcategories BEFORE setting form data
      setSubcategories(subcategoriesForForm);

      // Set form data
      setFormData((prev) => ({
        ...prev,
        title: product.title || '',
        mrp:
          (product.mrp && typeof product.mrp === 'object' ? product.mrp.standard : product.mrp) ||
          '',
        description: product.description || '',
        category: parentCategoryId,
        subcategory: subcategoryId,
        sku: product.sku || '',
        productCode: product.productCode || '',
        packagingType:
          product.packagingType && typeof product.packagingType === 'object'
            ? product.packagingType
            : { standard: '', master: '' },
        tax: {
          hsn_sac: product.tax?.hsn_sac || '',
          gstRate: product.tax?.gstRate || '',
        },
      }));

      // Set key features if they exist
      if (product.keyFeatures && product.keyFeatures.length > 0) {
        setKeyFeatures(product.keyFeatures);
      } else {
        setKeyFeatures([{ title: '', description: '' }]);
      }

      // TODO: Handle existing images - for now just reset
      setImages([]);
    }
  }, [productData, categories]);

  // Fixed useEffect - removed subcategories from dependencies to prevent infinite loop
  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const selectedCategory = categories.find((cat) => cat._id === formData.category);
      const newSubcategories = selectedCategory?.children || [];

      setSubcategories(newSubcategories);

      // Only clear subcategory if this is a manual change (not initial load)
      // and the current subcategory is not valid for the new category
      if (productData?.data && formData.subcategory) {
        const isValidSubcategory = newSubcategories.some((sub) => sub._id === formData.subcategory);
        if (!isValidSubcategory) {
          setFormData((prev) => ({ ...prev, subcategory: '' }));
        }
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category, categories, formData.subcategory, productData?.data]);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 15;
    const validImages = files.filter((file) => file.size <= 50 * 1024 * 1024);
    if (images.length + validImages.length <= maxFiles) {
      setImages([...images, ...validImages]);

      for (const file of validImages) {
        try {
          const uploadedUrl = await uploadFile(file);
          setImageUrls((prev) => [...prev, uploadedUrl]);
        } catch (error) {
          toastService.error(`Failed to upload ${file.name}`);
        }
      }
    } else {
      toastService.error('You can only upload up to 15 images (50MB each).');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Check if this is a packagingType or tax field
    if (name.startsWith('packagingType.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        packagingType: {
          ...prev.packagingType,
          [field]: value,
        },
      }));
    } else if (name.startsWith('tax.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        tax: {
          ...prev.tax,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Add new key feature field
  const addKeyFeature = () => {
    setKeyFeatures([...keyFeatures, { title: '', description: '' }]);
  };

  // Remove key feature field
  const removeKeyFeature = (index) => {
    if (keyFeatures.length > 1) {
      const updatedFeatures = [...keyFeatures];
      updatedFeatures.splice(index, 1);
      setKeyFeatures(updatedFeatures);
    }
  };

  // Handle key feature change
  const handleKeyFeatureChange = (index, field, value) => {
    const updatedFeatures = [...keyFeatures]; // Shallow copy of the array
    const updatedFeature = { ...updatedFeatures[index], [field]: value }; // Deep copy the object and update the field
    updatedFeatures[index] = updatedFeature; // Replace the old object with the new one in the array
    setKeyFeatures(updatedFeatures);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { title, mrp, description, category, subcategory, sku, productCode, packagingType, tax } =
      formData;

    if (!title || !mrp || !description || !category || !packagingType) {
      toastService.error('Please fill in all required fields.');
      return;
    }

    // Validate tax fields
    if (tax.gstRate && (tax.gstRate < 0 || tax.gstRate > 100)) {
      toastService.error('GST Rate must be between 0 and 100.');
      return;
    }

    if (tax.hsn_sac && (tax.hsn_sac.length < 4 || tax.hsn_sac.length > 8)) {
      toastService.error('HSN must be between 4 and 8 characters long.');
      return;
    }

    if (imageUrls.length !== images.length) {
      toastService.error('Please wait for all images to upload.');
      return;
    }

    try {
      const productUpdateData = {
        title,
        mrp: parseFloat(mrp),
        description,
        productCode,
        category: subcategory || category,
        packagingType,
        tax, // Include tax details
        keyFeatures: keyFeatures.filter((f) => f.title && f.description),
        images: [...existingImages, ...imageUrls], // Combine existing and new images
        imagesToDelete, // Add images to delete
        sku: sku.trim(), // Always send the trimmed SKU value
      };

      const response = await updateProduct({
        id,
        body: productUpdateData,
      }).unwrap();

      if (response.success) {
        toastService.success('Product updated successfully!');
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      console.error('Update error:', error);
      let errorMessage = 'Failed to update product';

      if (error.data) {
        errorMessage = error.data.message || errorMessage;
        if (error.data.errors) {
          errorMessage += ': ' + Object.values(error.data.errors).join(', ');
        }
      }

      toastService.error(errorMessage);
    }
  };

  if (loadingProduct || loadingCategories) {
    return <div className="p-4">Loading product details...</div>;
  }

  return (
    <div className="pt-0 px-4 sm:px-6 md:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 justify-center items-center">
        <h2 className="text-lg xl:text-2xl font-bold md:pl-5">Edit Product</h2>
        <Button
          onClick={() => navigate(-1)}
          className="mt-4 sm:mt-0 w-[40%] md:w-[30%] lg:w-[15%] xl:w-[10%]"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
      </div>

      <Card className="mt-2">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Title */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  Product Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-secondary">SKU</label>

                <div className="flex gap-2 items-center mt-1">
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    className="text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                  />
                  <button
                    type="button"
                    onClick={generateRandomSKU}
                    className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    title="Generate Random SKU"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {/* MRP */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  MRP (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="mrp"
                  value={formData.mrp}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
              {/* Product Code */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  Product Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleInputChange}
                  required
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>

              {/* Packaging Type */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary">
                    Package Quantity
                    <span className="text-xs font-normal ml-1 text-gray-500">
                      (Number of units per standard package)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="packagingType.standard"
                    value={formData.packagingType.standard}
                    onChange={handleInputChange}
                    placeholder="Enter quantity"
                    className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary">
                    Master Package Quantity
                    <span className="text-xs font-normal ml-1 text-gray-500">
                      (Number of units per shipping carton)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="packagingType.master"
                    value={formData.packagingType.master}
                    onChange={handleInputChange}
                    placeholder="Enter quantity"
                    className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    disabled={loadingCategories}
                    className="block text-sm w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color pr-10 appearance-none"
                  >
                    <option value="">Select a category</option>
                    {categories
                      .filter((category) => !category.parentCategory)
                      .map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Subcategory */}
              <div>
                <label className="block text-sm font-medium text-secondary">Subcategory</label>
                <div className="relative mt-1">
                  <select
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={handleInputChange}
                    disabled={!formData.category || subcategories.length === 0}
                    className="block text-sm w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color pr-10 appearance-none"
                  >
                    <option value="">
                      {subcategories.length ? 'Select a subcategory' : 'No subcategories available'}
                    </option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory._id} value={subcategory._id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
            </div>
          </div>

          {/* Tax Details */}
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4 border-b border-gray-200 pb-2">
              Tax Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* HSN */}
              <div>
                <label className="block text-sm font-medium text-secondary">HSN</label>
                <input
                  type="text"
                  name="tax.hsn_sac"
                  value={formData.tax.hsn_sac}
                  onChange={handleInputChange}
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>

              {/* GST Rate */}
              <div>
                <label className="block text-sm font-semibold text-secondary">GST Rate (%)</label>
                <input
                  type="number"
                  name="tax.gstRate"
                  value={formData.tax?.gstRate || ''}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
            </div>
          </div>

          {/* Key Features */}
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4 border-b border-gray-200 pb-2">
              Key Features
            </h3>
            <div className="space-y-4">
              {keyFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  {/* Left side - Feature Title */}
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Title</label>
                    <input
                      type="text"
                      placeholder="Eg. color"
                      value={feature.title}
                      onChange={(e) => handleKeyFeatureChange(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 border-color bg-white"
                    />
                  </div>

                  {/* Right side - Feature Description + Remove Button */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-secondary mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        placeholder="white"
                        value={feature.description}
                        onChange={(e) =>
                          handleKeyFeatureChange(index, 'description', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 border-color bg-white"
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <button
                        type="button"
                        onClick={() => removeKeyFeature(index)}
                        className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-md transition-colors duration-200"
                        title="Remove feature"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <Button type="button" onClick={addKeyFeature}>
                Add Feature
              </Button>
            </div>
          </div>

          {/* Product Images */}
          <div>
            <h3 className="text-base font-medium tertiary-text-color mb-2">Product Images</h3>
            <p className="text-sm text-gray-600 mb-2">
              Upload new images to replace existing ones (optional)
            </p>
            <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer">
              <UploadCloud size={36} className="text-gray-400 mb-2" />
              <p className="text-sm font-medium text-secondary mb-1">
                Drag & drop product images here
              </p>
              <p className="text-xs text-gray-500 mb-4">Support for JPG, PNG.</p>
              <div className="inline-block mt-2 px-3 py-1 primary-background white-text rounded-md shadow-sm text-sm lg:text-base">
                Select Files
              </div>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                multiple
                onChange={handleImageChange}
                className="hidden"
                disabled={uploading}
              />
            </label>

            {/* Existing Images Preview */}
            {existingImages.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-secondary mb-2">Existing Images</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {existingImages.map((img, i) => (
                    <div
                      key={img._id || i}
                      className="relative w-full h-32 rounded overflow-hidden border"
                    >
                      <img
                        src={img} // Corrected: img is the URL string itself
                        alt={`Existing ${i + 1}`}
                        className="object-cover w-full h-full cursor-pointer"
                        onClick={() => setSelectedImage(img)}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const imageToRemove = existingImages[i];
                          setImagesToDelete((prev) => [...prev, imageToRemove]);
                          const updated = existingImages.filter((_, index) => index !== i);
                          setExistingImages(updated);
                        }}
                        className="absolute top-1 right-1 bg-white text-gray-600 hover:text-[#832474] rounded-full p-1 shadow-sm"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Newly Uploaded Images Preview */}

            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {images.map((img, i) => (
                  <div key={i} className="relative w-full h-32 rounded overflow-hidden border">
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Preview ${i + 1}`}
                      className="object-cover w-full h-full cursor-pointer"
                      onClick={() => setSelectedImage(URL.createObjectURL(img))}
                    />
                    {imageUrls[i] ? (
                      <div className="absolute top-1 left-1 bg-green-500 white-text text-xs px-1 py-0.5 rounded">
                        ✓
                      </div>
                    ) : (
                      <div className="absolute top-1 left-1 bg-blue-500 white-text text-xs px-1 py-0.5 rounded">
                        ↑
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedImages = images.filter((_, index) => index !== i);
                        setImages(updatedImages);
                      }}
                      className="absolute top-1 right-1 bg-white text-gray-600 hover:text-[#832474] rounded-full p-1 shadow-sm"
                      disabled={uploading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              className="text-sm"
              type="submit"
              disabled={
                isLoading || uploading || (images.length > 0 && imageUrls.length !== images.length)
              }
            >
              {isLoading ? 'Updating...' : uploading ? 'Uploading...' : 'Update Product'}
            </Button>
          </div>
        </form>
        <DocumentPreview
          isOpen={!!selectedImage}
          fileUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
          fileType={selectedImage ? selectedImage.split('.').pop() : ''}
        />
      </Card>
    </div>
  );
};

export default EditProduct;
